// src/quality-management/components/OvertimeApprovalDashboard.tsx
// Refatorado com a nova identidade visual (Tailwind + UI kit)

import React, { useState, useEffect, useContext } from 'react';
import { Task, OvertimeApprovalRequest } from '../services/api/types';
import { apiService } from '../services/api';
import { AuthContext } from '../contexts/AuthAdapter';
import {
  Button,
  Card,
  Badge,
  Textarea,
  Modal,
  Spinner,
  EmptyState,
} from '../../shared/components/ui';
import './OvertimeApprovalDashboard.css';
import './components.css';

interface OvertimeApprovalDashboardProps {
  onApprovalAction?: () => void;
}

// Formata uma data que pode vir como 'YYYY-MM-DD' ou ISO completo, sem gerar "Invalid Date"
const formatTaskDate = (value?: string | null): string => {
  if (!value) return '—';
  const iso = value.includes('T') ? value : `${value}T00:00:00`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Formata horas, tratando valores ausentes
const formatHours = (value?: number | null): string =>
  value === null || value === undefined ? '—' : `${value}h`;

const OvertimeApprovalDashboard: React.FC<OvertimeApprovalDashboardProps> = ({ onApprovalAction }) => {
  const { user } = useContext(AuthContext);
  const [pendingTasks, setPendingTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingTaskId, setProcessingTaskId] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const tasks = await apiService.tasks.getPendingOvertimeApprovals();
      // Mostra apenas solicitações que realmente aguardam aprovação (não as já processadas)
      const actionable = (Array.isArray(tasks) ? tasks : []).filter(
        (t) =>
          t.overtime_approval_status === 'pending_supervisor' ||
          t.overtime_approval_status === 'pending_manager'
      );
      setPendingTasks(actionable);
    } catch (error) {
      console.error('Error fetching pending overtime approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalClick = (task: Task, action: 'approve' | 'reject') => {
    setSelectedTask(task);
    setApprovalAction(action);
    setApprovalNotes('');
    setShowApprovalModal(true);
  };

  const handleSubmitApproval = async () => {
    if (!selectedTask) return;

    try {
      setProcessingTaskId(selectedTask.id);

      const approvalData: OvertimeApprovalRequest = {
        action: approvalAction,
        notes: approvalNotes.trim() || undefined
      };

      await apiService.tasks.approveOvertime(selectedTask.id, approvalData);
      await fetchPendingApprovals();

      setShowApprovalModal(false);
      setSelectedTask(null);
      setApprovalNotes('');

      if (onApprovalAction) {
        onApprovalAction();
      }
    } catch (error) {
      console.error('Error processing overtime approval:', error);
      alert('Erro ao processar aprovação. Tente novamente.');
    } finally {
      setProcessingTaskId(null);
    }
  };

  const getApprovalStageText = (task: Task): string => {
    switch (task.overtime_approval_status) {
      case 'pending_supervisor':
        return 'Aguardando Supervisor';
      case 'pending_manager':
        return 'Aguardando Gerente';
      case 'approved':
        return 'Aprovado';
      case 'rejected':
        return 'Rejeitado';
      default:
        return 'Status Desconhecido';
    }
  };

  const getStatusTone = (task: Task): 'neutral' | 'success' | 'warning' | 'danger' | 'info' => {
    switch (task.overtime_approval_status) {
      case 'pending_supervisor':
        return 'info';
      case 'pending_manager':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const canUserApprove = (task: Task): boolean => {
    if (!user) return false;

    const userRole = user.role?.toLowerCase();
    const status = task.overtime_approval_status;

    if (status === 'pending_supervisor') {
      return ['supervisor', 'manager', 'director', 'admin'].includes(userRole) ||
        userRole.includes('supervisor') ||
        userRole.includes('manager') ||
        userRole.includes('director');
    } else if (status === 'pending_manager') {
      return ['manager', 'director', 'admin'].includes(userRole) ||
        userRole.includes('manager') ||
        userRole.includes('director');
    }

    return false;
  };

  const getOvertimeTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'regular_overtime': 'Hora Extra Regular (50%)',
      'weekend_holiday': 'Fim de Semana/Feriado (100%)',
      'night_work': 'Trabalho Noturno (20%)',
      'weekly_rest_dsr': 'DSR (100%)',
      'travel_time': 'Tempo de Viagem (50%)',
      'hazardous_work': 'Trabalho Perigoso (40%)',
      'intermittent_work': 'Trabalho Intermitente (25%)'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <Spinner size="lg" />
        <h3 className="font-display text-lg font-semibold text-foreground">Carregando aprovações...</h3>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="font-display text-2xl font-semibold text-foreground">
          Aprovação de Horas Extras
        </h2>
        <p className="mt-1 text-base text-foreground-muted">
          Revise e aprove solicitações de hora extra • {pendingTasks.length} pendente{pendingTasks.length !== 1 ? 's' : ''}
        </p>
      </div>

      {pendingTasks.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            }
            title="Nenhuma aprovação pendente"
            description="Todas as solicitações de hora extra foram processadas."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {pendingTasks.map(task => (
            <Card key={task.id} padding="lg" data-testid="overtime-approval-row" className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-base font-semibold leading-snug text-foreground">{task.name}</h3>
                <Badge tone={getStatusTone(task)} size="sm">
                  {getApprovalStageText(task)}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">Horas extras</span>
                  <span className="text-base font-semibold tabular-nums text-foreground">{formatHours(task.overtime_hours)}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">Pagamento</span>
                  <span className="text-base font-semibold tabular-nums text-foreground">{(task.calculated_overtime_pay || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">Tipo</span>
                  <span className="truncate text-sm text-foreground">{getOvertimeTypeLabel(task.overtime_type || '')}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">Data</span>
                  <span className="text-sm tabular-nums text-foreground">{formatTaskDate(task.task_date)}</span>
                </div>
              </div>

              {task.overtime_justification && (
                <div className="rounded-lg border border-border bg-surface-2 px-3 py-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">Justificativa</div>
                  <div className="mt-0.5 line-clamp-3 text-sm text-foreground">{task.overtime_justification}</div>
                </div>
              )}

              {canUserApprove(task) && (
                <div className="mt-auto grid grid-cols-2 gap-2.5 pt-1">
                  <button
                    type="button"
                    data-testid="overtime-reject"
                    onClick={() => handleApprovalClick(task, 'reject')}
                    disabled={processingTaskId === task.id}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-danger/40 bg-danger/5 px-3 py-2.5 text-sm font-semibold text-danger transition-colors hover:bg-danger hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 disabled:opacity-50"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                    Rejeitar
                  </button>
                  <button
                    type="button"
                    data-testid="overtime-approve"
                    onClick={() => handleApprovalClick(task, 'approve')}
                    disabled={processingTaskId === task.id}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-success px-3 py-2.5 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-success/60 transition hover:brightness-110 active:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-success/50 disabled:opacity-50"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                    Aprovar
                  </button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showApprovalModal && !!selectedTask}
        onClose={() => setShowApprovalModal(false)}
        title={`${approvalAction === 'approve' ? 'Aprovar' : 'Rejeitar'} Hora Extra`}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowApprovalModal(false)}
              disabled={!!selectedTask && processingTaskId === selectedTask.id}
            >
              Cancelar
            </Button>
            <Button
              variant={approvalAction === 'approve' ? 'success' : 'danger'}
              onClick={handleSubmitApproval}
              disabled={!!selectedTask && processingTaskId === selectedTask.id}
              loading={!!selectedTask && processingTaskId === selectedTask.id}
            >
              {approvalAction === 'approve' ? 'Aprovar' : 'Rejeitar'}
            </Button>
          </>
        }
      >
        {selectedTask && (
          <div className="flex flex-col gap-5">
            <div className="rounded-lg border border-border bg-surface-2 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Tarefa</p>
              <p className="mt-1 font-display text-base font-semibold text-foreground">{selectedTask.name}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Horas</span>
                <span className="text-base font-semibold tabular-nums text-foreground">{formatHours(selectedTask.overtime_hours)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Pagamento</span>
                <span className="text-base font-semibold tabular-nums text-foreground">{(selectedTask.calculated_overtime_pay || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
              </div>
            </div>

            <Textarea
              data-testid="overtime-notes"
              label={`${approvalAction === 'approve' ? 'Notas de Aprovação' : 'Motivo da Rejeição'} (Opcional)`}
              rows={4}
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder={`Adicionar ${approvalAction === 'approve' ? 'notas' : 'motivo'}...`}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OvertimeApprovalDashboard;
